import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ProfitabilitySummaryResponse } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';
import {
  computeTotalCosts,
  getProfitabilityCostSegments,
  type ProfitabilityCostSegment,
} from '../../utils/profitabilityCosts';

interface ProfitabilityCostsChartProps {
  data?: ProfitabilitySummaryResponse;
  isLoading?: boolean;
  locale: string;
  onOpenCogs?: () => void;
  onOpenShipping?: () => void;
}

interface ChartSegment extends ProfitabilityCostSegment {
  name: string;
  percentage: number;
  formattedValue: string;
  [key: string]: string | number;
}

/** Misma rejilla que la fila principal de KPIs (ProfitabilityKpiSection). */
export const PROFITABILITY_SUMMARY_GRID = 'grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-5';

const costsCardClass =
  'flex h-full flex-col rounded-xl border border-border bg-card-bg p-4 shadow-sm transition-colors hover:border-primary/20 hover:shadow-md';

function CostsCardTitle({
  title,
  tooltip,
  tooltipTestId,
}: {
  title: string;
  tooltip?: string;
  tooltipTestId?: string;
}) {
  return (
    <div className="mb-3 flex min-h-[1.25rem] items-center gap-1.5">
      <h3 className="truncate text-xs font-medium text-muted-foreground">{title}</h3>
      {tooltip && tooltipTestId && (
        <InfoTooltip text={tooltip} dataTestId={tooltipTestId} iconClassName="h-3 w-3 shrink-0" />
      )}
    </div>
  );
}

function CostsChartSkeleton() {
  return (
    <div className={PROFITABILITY_SUMMARY_GRID} data-testid="profitability-costs-section-skeleton">
      <div className={cn(costsCardClass, 'animate-pulse sm:col-span-3 lg:col-span-2')}>
        <div className="mb-3 h-3 w-28 rounded bg-muted/60" />
        <div className="flex flex-1 items-center justify-center py-2">
          <div className="aspect-square w-full max-w-[12rem] rounded-full bg-muted/40" />
        </div>
      </div>
      <div className={cn(costsCardClass, 'animate-pulse sm:col-span-3 lg:col-span-3')}>
        <div className="mb-3 h-3 w-24 rounded bg-muted/60" />
        <div className="space-y-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <div className="h-3 w-28 rounded bg-muted/60" />
              <div className="h-3 w-16 rounded bg-muted/60" />
              <div className="h-3 w-8 rounded bg-muted/60" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const ProfitabilityCostsChart = ({
  data,
  isLoading,
  locale,
  onOpenCogs,
  onOpenShipping,
}: ProfitabilityCostsChartProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const segmentLabels = useMemo(
    () => ({
      ad_spend: t('summary.kpis.ad_spend_total'),
      cogs: t('summary.kpis.cogs'),
      shipping: t('summary.kpis.shipping_costs'),
      taxes: t('summary.kpis.taxes'),
    }),
    [t],
  );

  const { totalCosts, chartSegments } = useMemo(() => {
    if (!data) {
      return { totalCosts: 0, chartSegments: [] as ChartSegment[] };
    }

    const total = computeTotalCosts(data.totals);
    const segments = getProfitabilityCostSegments(data.totals).map((segment) => ({
      ...segment,
      name: segmentLabels[segment.id],
      formattedValue: formatProfitabilityAmount(segment.value, locale, currency),
      percentage: total > 0 ? (segment.value / total) * 100 : 0,
    }));

    return { totalCosts: total, chartSegments: segments };
  }, [currency, data, locale, segmentLabels]);

  const pieData = useMemo(() => chartSegments.filter((segment) => segment.value > 0), [chartSegments]);

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartSegment }> }) => {
    if (!active || !payload?.length) return null;

    const item = payload[0].payload;
    return (
      <div className="z-50 rounded-md border border-border bg-card-bg p-3 shadow-lg">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: item.color }} />
          <div>
            <p className="font-medium text-foreground">{item.name}</p>
            <p className="text-sm text-muted-foreground">
              {item.formattedValue} ({item.percentage.toFixed(1)} %)
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <CostsChartSkeleton />;
  }

  return (
    <div className={PROFITABILITY_SUMMARY_GRID} data-testid="profitability-costs-section">
      <div className={cn(costsCardClass, 'sm:col-span-3 lg:col-span-2')} data-testid="profitability-costs-chart">
        <CostsCardTitle
          title={t('summary.costs_chart.title')}
          tooltip={t('summary.costs_chart.hint')}
          tooltipTestId="profitability-costs-chart-tooltip"
        />

        {pieData.length === 0 ? (
          <p className="flex flex-1 items-center justify-center py-6 text-center text-sm text-muted-foreground">
            {t('summary.costs_chart.empty')}
          </p>
        ) : (
          <div className="flex flex-1 items-center justify-center py-1">
            <div className="relative aspect-square w-full max-w-[13rem]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius="58%"
                    outerRadius="82%"
                    paddingAngle={1}
                    dataKey="value"
                    isAnimationActive={false}
                    stroke="none"
                    onMouseEnter={(_, index) => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={entry.id}
                        fill={entry.color}
                        stroke="none"
                        style={{
                          transform: hoveredIndex === index ? 'scale(1.03)' : 'scale(1)',
                          transformOrigin: 'center',
                          transition: 'transform 0.2s ease-in-out',
                        }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} offset={20} wrapperStyle={{ pointerEvents: 'none' }} />
                </PieChart>
              </ResponsiveContainer>

              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-2 text-center">
                <span className="text-[10px] font-medium uppercase leading-none tracking-wide text-muted-foreground">
                  {t('summary.costs_chart.total')}
                </span>
                <span className="mt-1 text-sm font-semibold tabular-nums leading-tight text-foreground">
                  {formatProfitabilityAmount(totalCosts, locale, currency)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={cn(costsCardClass, 'sm:col-span-3 lg:col-span-3')} data-testid="profitability-costs-table">
        <CostsCardTitle title={t('summary.costs_chart.table_title')} />

        <div className="flex flex-1 flex-col justify-center overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs text-muted-foreground">{t('summary.costs_chart.col_concept')}</TableHead>
                <TableHead className="text-right text-xs text-muted-foreground">
                  {t('summary.costs_chart.col_amount')}
                </TableHead>
                <TableHead className="w-14 text-right text-xs text-muted-foreground">
                  {t('summary.costs_chart.col_share')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chartSegments.map((segment) => {
                const pieIndex = pieData.findIndex((item) => item.id === segment.id);
                const onSegmentClick =
                  segment.id === 'cogs' ? onOpenCogs : segment.id === 'shipping' ? onOpenShipping : undefined;
                const clickable = !!onSegmentClick;

                return (
                  <TableRow
                    key={segment.id}
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={clickable ? onSegmentClick : undefined}
                    onKeyDown={
                      clickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSegmentClick?.();
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      'border-border transition-colors',
                      pieIndex >= 0 && hoveredIndex === pieIndex && 'bg-muted/50',
                      clickable &&
                        'cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    )}
                    data-testid={`profitability-costs-legend-${segment.id}`}
                    onMouseEnter={() => {
                      if (pieIndex >= 0) setHoveredIndex(pieIndex);
                    }}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <TableCell className="py-2.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                          style={{ backgroundColor: segment.color }}
                        />
                        <span className="truncate text-sm text-foreground">{segment.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 text-right tabular-nums text-sm font-medium text-foreground">
                      {segment.formattedValue}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'py-2.5 text-right tabular-nums text-sm text-muted-foreground',
                        segment.value <= 0 && 'opacity-60',
                      )}
                    >
                      {segment.value > 0 && totalCosts > 0 ? `${segment.percentage.toFixed(1)}%` : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
